import React from "react";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { useSendVoorraadverschilWebhook } from "@/hooks/notifications/useSendVoorraadverschilWebhook";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";

// UUID validation helper
const isValidUUID = (str: string | undefined): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

interface SendWebhookButtonProps {
  notificationId: string;
  disabled?: boolean;
}

const SendWebhookButton = ({ notificationId, disabled }: SendWebhookButtonProps) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const handleSendWebhook = async () => {
    setIsLoading(true);
    console.log("Manual webhook trigger started for notification:", notificationId);
    
    try {
      // Only send triggered_by if it's a valid UUID
      const body: any = {
        notification_id: notificationId,
      };
      
      if (isValidUUID(user?.id)) {
        body.triggered_by = user?.id;
        body.triggered_by_name = user?.name;
      }
      
      const { data, error } = await supabase.functions.invoke('send-voorraadverschil-webhook', {
        body
      });

      console.log("Webhook response:", { data, error });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        toast.error("Webhook verzending mislukt", {
          description: data?.body ? `Status: ${data.status} - ${data.body.substring(0, 120)}` : `Status: ${data?.status}`
        });
      } else {
        toast.success("Voorraadverschil webhook verzonden", {
          description: `Status: ${data.status} - Succesvol verzonden`
        });
      }

      // Refresh webhook logs
      queryClient.invalidateQueries({ queryKey: ['webhook-logs', notificationId] });
    } catch (error: any) {
      console.error("Error sending webhook:", error);
      toast.error("Fout bij verzenden webhook", {
        description: error.message || "Onbekende fout"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleSendWebhook}
      disabled={disabled || isLoading}
      variant="outline"
      size="sm"
    >
      <Send className="h-4 w-4 mr-2" />
      {isLoading ? "Verzenden..." : "Stuur webhook"}
    </Button>
  );
};

export default SendWebhookButton;
